import React, { useState } from "react";
import * as HeroIcons from "@heroicons/react/24/outline";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "components/ui/button";
import { DataTableColumnHeader } from "components/ui/data-table-column-header";
import { createStandardHeader } from "components/ui/standard-column-header";
import { titleCase, upperCase } from "lib/utils";
import { CnDocumentModal } from "../in-transit/CnDocumentModal";

interface DeliveredShipment {
  cn_number: string;
  cn_date: string;
  branch_name: string;
  consignor: string;
  consignee: string;
  origin: string;
  destination: string;
  delivery_date: string;
  entered_by?: string | null;
  cn_distance_km: number;
  status: string;
  pod_link?: string | null;
  invoice_link?: string | null;
}

const DocumentsCell: React.FC<{ shipment: DeliveredShipment }> = ({ shipment }) => {
  const openLink = (url?: string | null) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex items-center space-x-1">
      <Button
        variant="outline"
        size="sm"
        className={`h-7 px-2.5 py-1 text-xs flex items-center gap-1 ${
          shipment.pod_link
            ? "bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200 hover:text-blue-700"
            : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
        }`}
        onClick={() => openLink(shipment.pod_link)}
        disabled={!shipment.pod_link}
        title={shipment.pod_link ? "Open POD" : "No POD available"}
      >
        <HeroIcons.DocumentCheckIcon className="h-4 w-4" />
        <span>POD</span>
      </Button>

      <Button
        variant="outline"
        size="sm"
        className={`h-7 px-2.5 py-1 text-xs flex items-center gap-1 ${
          shipment.invoice_link
            ? "bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200 hover:text-blue-700"
            : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
        }`}
        onClick={() => openLink(shipment.invoice_link)}
        disabled={!shipment.invoice_link}
        title={shipment.invoice_link ? "Open Invoice" : "No Invoice available"}
      >
        <HeroIcons.DocumentTextIcon className="h-4 w-4" />
        <span>Invoice</span>
      </Button>
    </div>
  );
};

const CnNumberCell: React.FC<{ cnNumber: string }> = ({ cnNumber }) => {
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);

  return (
    <div className="flex items-center space-x-2 w-[120px]">
      <span className="font-medium text-xs">{cnNumber}</span>
      <HeroIcons.DocumentTextIcon
        className="h-4 w-4 text-gray-500 cursor-pointer hover:text-gray-800"
        onClick={() => setIsDocModalOpen(true)}
      />
      <CnDocumentModal
        isOpen={isDocModalOpen}
        onClose={() => setIsDocModalOpen(false)}
        cnNumber={cnNumber}
      />
    </div>
  );
};

export const deliveredColumns: ColumnDef<DeliveredShipment>[] = [
  {
    accessorKey: "cn_number",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CN Number" />
    ),
    cell: ({ row }) => {
      const cnNumber = row.getValue("cn_number") as string;
      return <CnNumberCell cnNumber={cnNumber} />;
    },
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: "cn_date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CN Date" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.getValue("cn_date"));
      const formatted = date.toLocaleDateString("en-US", {
        month: "short", 
        day: "numeric", 
        year: "numeric", 
      });
      return <div className="w-[100px] text-xs">{formatted}</div>
    },
    enableSorting: true,
  },
  {
    accessorKey: "branch_name",
    header: createStandardHeader("Branch"),
    cell: ({ row }) => {
      const branchName = row.getValue("branch_name") as string
      return (
        <div className="max-w-[120px] truncate text-xs" title={branchName}>
          {titleCase(branchName)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "consignor",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consignor" />
    ),
    cell: ({ row }) => {
      const consignor = row.getValue("consignor") as string
      return (
        <div className="max-w-[150px] truncate text-xs" title={consignor}>
          {upperCase(consignor)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "consignee",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consignee" />
    ),
    cell: ({ row }) => {
      const consignee = row.getValue("consignee") as string
      return (
        <div className="max-w-[150px] truncate text-xs" title={consignee}>
          {upperCase(consignee)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "origin",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Origin" />
    ),
    cell: ({ row }) => {
      const origin = row.getValue("origin") as string
      return (
        <div className="max-w-[120px] truncate text-xs" title={origin}>
          {titleCase(origin)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "destination",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Destination" />
    ),
    cell: ({ row }) => {
      const destination = row.getValue("destination") as string
      return (
        <div className="max-w-[120px] truncate text-xs" title={destination}>
          {titleCase(destination)}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "cn_distance_km",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CN Distance" />
    ),
    cell: ({ row }) => {
      const totalDistance = row.getValue("cn_distance_km");
      return (
        <div className="w-[70px] text-xs">
          {totalDistance ? `${totalDistance} km` : "-"}
        </div>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: "delivery_date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Delivery Date" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.getValue("delivery_date"));
      const formatted = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const status = row.original.status;
      const isDelayed = status === "delayed"
      return (
        <div className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs ${
            isDelayed
              ? "bg-red-100 text-red-600 border border-red-200"
              : "bg-green-100 text-green-700 border border-green-200"
          }`} >
          {formatted}
        </div>
      )
    },
    enableSorting: true,
  },
  {
    accessorKey: "entered_by",
    header: createStandardHeader("Entered By"),
    cell: ({ row }) => {
      const enteredBy = row.getValue("entered_by") as string | null;

      return (
        <div
          className="max-w-[180px] truncate text-xs"
          title={enteredBy || ""}
        >
          {enteredBy || "-"}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: "documents",
    header: createStandardHeader("Documents"),
    cell: ({ row }) => <DocumentsCell shipment={row.original} />,
    enableSorting: false,
    enableHiding: false,
  },
]
